/**
 * Context-engineering + alerting tunables — situation-block guards, the context-fill warning, the summarization pipeline, and
 * the in-app alert engine. Aggregated by OnestoneConfig in ./config-root.
 */

import { defaultsOf, resolveSection, type SectionValues } from '../settings/registry.js';
import {
  ALERT_CATEGORIES,
  ALERT_SETTINGS,
  CONTEXT_SETTINGS,
  SITUATION_CAP_SETTINGS,
  SITUATION_SLICE_SETTINGS,
  SUMMARIZE_SETTINGS,
} from '../settings/sections.js';

// ---------------------------------------------------------------------------
// Situation-block tunables
// ---------------------------------------------------------------------------

/** Per-section overflow caps for the per-turn situation block (chars). 0 = no cap. */
export type SituationSectionCaps = SectionValues<typeof SITUATION_CAP_SETTINGS>;
/** Item limits applied before the situation block formats a section. 0 = no limit. */
export type SituationSliceLimits = SectionValues<typeof SITUATION_SLICE_SETTINGS>;

export interface SituationConfig {
  sectionCaps: SituationSectionCaps;
  sliceLimits: SituationSliceLimits;
}

export const DEFAULT_SITUATION_SECTION_CAPS: SituationSectionCaps = defaultsOf(SITUATION_CAP_SETTINGS);
export const DEFAULT_SITUATION_SLICE_LIMITS: SituationSliceLimits = defaultsOf(SITUATION_SLICE_SETTINGS);
export const DEFAULT_SITUATION_CONFIG: SituationConfig = {
  sectionCaps: { ...DEFAULT_SITUATION_SECTION_CAPS },
  sliceLimits: { ...DEFAULT_SITUATION_SLICE_LIMITS },
};

export function resolveSituationConfig(stored: unknown): SituationConfig {
  const s = (stored ?? {}) as { sectionCaps?: unknown; sliceLimits?: unknown };
  return {
    sectionCaps: resolveSection(SITUATION_CAP_SETTINGS, s.sectionCaps),
    sliceLimits: resolveSection(SITUATION_SLICE_SETTINGS, s.sliceLimits),
  };
}

/** The context-fill warning (`context`). Only the operator condenses a conversation (Summarize). */
export type ContextConfig = SectionValues<typeof CONTEXT_SETTINGS>;
export const DEFAULT_CONTEXT_CONFIG: ContextConfig = defaultsOf(CONTEXT_SETTINGS);
export function resolveContextConfig(stored: unknown): ContextConfig {
  return resolveSection(CONTEXT_SETTINGS, stored);
}

// ---------------------------------------------------------------------------
// Summarization pipeline tunables
// ---------------------------------------------------------------------------

/** Summarize-pipeline settings (`summarize`). */
export type SummarizeConfig = SectionValues<typeof SUMMARIZE_SETTINGS>;
/** Multi-chunk summarization strategy. */
export type SummarizeStrategy = SummarizeConfig['strategy'];
export const DEFAULT_SUMMARIZE_CONFIG: SummarizeConfig = defaultsOf(SUMMARIZE_SETTINGS);
export function resolveSummarizeConfig(stored: unknown): SummarizeConfig {
  return resolveSection(SUMMARIZE_SETTINGS, stored);
}

// ---------------------------------------------------------------------------
// Alerting — lightweight in-app alert engine
// ---------------------------------------------------------------------------

/** Alert severity — determines badge color and sort order. */
export type AlertSeverity = 'critical' | 'warning' | 'info';

/** Alert category — each maps to one threshold check. */
export type AlertCategory = (typeof ALERT_CATEGORIES)[number];

/** A single active alert — derived from ops-log data, not stored. */
export interface Alert {
  id: string;
  severity: AlertSeverity;
  category: AlertCategory;
  title: string;
  detail: string;
  /** ISO timestamp when this alert was first detected in the current window. */
  firstSeen: string;
}

/** Alert-engine settings (`alerts`). 0 turns a threshold check off. */
export type AlertConfig = SectionValues<typeof ALERT_SETTINGS>;
export const DEFAULT_ALERT_CONFIG: AlertConfig = defaultsOf(ALERT_SETTINGS);
export function resolveAlertConfig(stored: unknown): AlertConfig {
  return resolveSection(ALERT_SETTINGS, stored);
}
